import { ReactElement } from 'react';
import { Typography } from '@mui/material';
import { largeCurrencyFormat, smallCurrencyFormat } from 'common/formats/formats';
import { GridType, Row } from 'common/types/scalarSpreadsheet';
import ReviewPriceCell from 'pages/Intelligence/DailyNav/components/ReviewPriceCell';
import {
  DAILY_NAV_COLUMN_LABELS,
  DAILY_NAV_COLUMN_TYPES,
  DAILY_NAV_SECTIONS,
  DAILY_NAV_SPREADSHEET_SECTIONS_ALIAS,
  DAILY_NAV_SPREADSHEET_TITLE_ALIAS,
} from 'pages/Intelligence/DailyNav/dailyNav.constants';
import { getExpression, getObjectValue } from 'utilities';
import { alphabetGenerator } from 'utilities/alphabet-utilities';
import { DailyNavColumn, DailyNavTableRow } from '../../useDailyNavSheet.types';

// Map column index -> section band cell config (label + span at each section start).
const getSectionBandByColumnIndex = (columnTypeOrder: string[]) => {
  const bandByIndex = new Map<number, { label: string; colSpan: number }>();
  DAILY_NAV_SECTIONS.forEach(section => {
    const memberIndexes = section.columnTypes
      .map(columnType => columnTypeOrder.indexOf(columnType))
      .filter(index => index >= 0)
      .sort((a, b) => a - b);
    if (memberIndexes.length) {
      bandByIndex.set(memberIndexes[0], { label: section.label, colSpan: memberIndexes.length });
    }
  });
  return bandByIndex;
};

const SectionBandLabel = ({ label }: { label: string }) => (
  <Typography
    variant="caption"
    sx={{
      color: 'text.secondary',
      display: 'block',
      fontWeight: 600,
      letterSpacing: 0.75,
      textAlign: 'center',
      textTransform: 'uppercase',
    }}>
    {label}
  </Typography>
);

interface CellParserParams {
  column: DailyNavColumn;
  columnType: string;
  colIndex: number;
  key: string;
  row: Row<object>;
  rowByAlias: Record<string, DailyNavTableRow>;
  sectionBandByColumnIndex: Map<number, { label: string; colSpan: number }>;
  onReviewClick: (row: DailyNavTableRow) => void;
  canReview: boolean;
}

interface DailyNavCell extends Row<object> {
  key: string;
  format?: object | null;
  gridType?: GridType;
  component?: ReactElement | null;
  forceComponent?: boolean;
  colSpan?: number | null;
  customFormat?: { currency: { code: string } };
}

// Values are stored in each company's cap-table currency; default to USD when a row
// has no snapshot currency so the symbol never renders blank.
const DEFAULT_CURRENCY_CODE = 'USD';

const cellParser = (params: CellParserParams): DailyNavCell => {
  const { column, columnType, colIndex, key, row, rowByAlias, sectionBandByColumnIndex, onReviewClick, canReview } =
    params;
  const { alias: rowAlias } = getObjectValue(row);

  const columnCellValue = getObjectValue(column[String(rowAlias)]).value;
  const cell = { ...row, key, value: columnCellValue } as DailyNavCell;

  if (rowAlias === DAILY_NAV_SPREADSHEET_SECTIONS_ALIAS) {
    const band = sectionBandByColumnIndex.get(colIndex);
    cell.value = '';
    if (band) {
      cell.colSpan = band.colSpan;
      cell.component = <SectionBandLabel label={band.label} />;
      cell.forceComponent = true;
    }
    return cell;
  }

  if (rowAlias === DAILY_NAV_SPREADSHEET_TITLE_ALIAS) {
    cell.value = DAILY_NAV_COLUMN_LABELS[columnType] ?? columnType;
    return cell;
  }

  // Per-row currency: each company's monetary cells render in its own cap-table
  // currency rather than a spreadsheet-wide default. FastValueViewer resolves a
  // number cell's currency from customFormat.currency (it ignores cell.currency and
  // spreads the merged currency last), so the code must be injected there.
  const rowCurrency = rowByAlias[String(rowAlias)]?.lookingGlass?.cap_table_currency || DEFAULT_CURRENCY_CODE;
  const applyCurrency = () => {
    cell.customFormat = { currency: { code: rowCurrency } };
  };

  switch (columnType) {
    case DAILY_NAV_COLUMN_TYPES.EQUITY_VALUE:
      // Companies with no Looking Glass snapshot should read as blank, not $0.
      if (columnCellValue == null) {
        cell.gridType = 'string';
        cell.value = '';
      } else {
        cell.format = largeCurrencyFormat;
        cell.gridType = 'number';
        applyCurrency();
      }
      break;

    case DAILY_NAV_COLUMN_TYPES.PRICE_PER_SHARE:
      if (columnCellValue == null) {
        cell.gridType = 'string';
        cell.value = '';
      } else {
        cell.format = smallCurrencyFormat;
        cell.gridType = 'number';
        applyCurrency();
      }
      break;

    case DAILY_NAV_COLUMN_TYPES.STATUS: {
      const dailyNavRow = rowByAlias[String(rowAlias)];
      cell.gridType = 'string';
      if (dailyNavRow) {
        cell.component = <ReviewPriceCell row={dailyNavRow} onReviewClick={onReviewClick} canReview={canReview} />;
        cell.forceComponent = true;
      }
      break;
    }

    default:
      cell.gridType = 'string';
      break;
  }

  return cell;
};

interface CreateParserParams {
  columns: DailyNavColumn[];
  rowConfig: Row<object>[];
  dailyNavRows: DailyNavTableRow[];
  onReviewClick: (row: DailyNavTableRow) => void;
  columnTypeOrder: string[];
  canReview: boolean;
}

const createParser = (params: CreateParserParams): Record<string, DailyNavCell> => {
  const { columns, rowConfig, dailyNavRows, onReviewClick, columnTypeOrder, canReview } = params;

  const cells: Record<string, DailyNavCell> = {};
  const alphabet = alphabetGenerator([], columns.length);
  const sectionBandByColumnIndex = getSectionBandByColumnIndex(columnTypeOrder);

  const rowByAlias: Record<string, DailyNavTableRow> = dailyNavRows.reduce(
    (accumulator, dailyNavRow) => ({ ...accumulator, [String(dailyNavRow.companyId)]: dailyNavRow }),
    {}
  );

  rowConfig.forEach((row, rowIndex) => {
    columns.forEach((column, colIndex) => {
      const rowNumber = rowIndex + 1;
      const columnLegend = alphabet[colIndex];
      const columnType = columnTypeOrder[colIndex] ?? '';
      const key = columnLegend + rowNumber;

      const cell = cellParser({
        column,
        columnType,
        colIndex,
        key,
        row,
        rowByAlias,
        sectionBandByColumnIndex,
        onReviewClick,
        canReview,
      });
      cell.expr = getExpression({ expr: String(cell.expr ?? cell.value ?? ''), columnLegend });

      cells[key] = cell;
    });
  });

  return cells;
};

export default createParser;
